// src/components/common/Toast.js
import React from 'react';
import { toast, Slide, Bounce, Flip, Zoom } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FiCheckCircle,
    FiAlertCircle,
    FiInfo,
    FiAlertTriangle,
    FiX
} from 'react-icons/fi';

/**
 * Custom Toast Component
 * A fully customizable wrapper around react-toastify
 */

// Custom toast content component
const CustomToastContent = ({ type, message, title, icon: CustomIcon }) => {
    const icons = {
        success: FiCheckCircle,
        error: FiAlertCircle,
        info: FiInfo,
        warning: FiAlertTriangle,
    };

    const colors = {
        success: 'text-green-500',
        error: 'text-red-500',
        info: 'text-blue-500',
        warning: 'text-yellow-500',
    };

    const Icon = CustomIcon || icons[type] || FiInfo;
    const iconColor = colors[type] || colors.info;

    return (
        <div className="flex items-start gap-3">
            <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
                {title && (
                    <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
                )}
                <p className="text-sm text-gray-700">{message}</p>
            </div>
        </div>
    );
};

// Default toast options
const defaultOptions = {
    position: 'bottom-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    transition: Slide,
    icon: false, // Disable default react-toastify icon
    closeButton: ({ closeToast }) => (
        <button
            onClick={closeToast}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
        >
            <FiX className="w-4 h-4" />
        </button>
    ),
};

/**
 * Show a toast notification
 * @param {Object} options - Toast configuration
 * @param {string} options.message - The message to display (required)
 * @param {string} options.type - Toast type: 'success', 'error', 'info', 'warning' (default: 'info')
 * @param {string} options.title - Optional title
 * @param {Component} options.icon - Custom icon component (optional)
 * @param {string} options.position - Position: 'top-left', 'top-right', 'top-center', 'bottom-left', 'bottom-right', 'bottom-center' (default: 'top-right')
 * @param {number} options.autoClose - Auto close duration in ms (default: 3000, false to disable)
 * @param {boolean} options.hideProgressBar - Hide progress bar (default: false)
 * @param {boolean} options.closeOnClick - Close on click (default: true)
 * @param {boolean} options.pauseOnHover - Pause on hover (default: true)
 * @param {boolean} options.draggable - Enable dragging (default: true)
 * @param {string} options.transition - Transition type: 'Slide', 'Bounce', 'Flip', 'Zoom' (default: 'Slide')
 * @param {string} options.theme - Theme: 'light', 'dark', 'colored' (default: 'light')
 * @param {string} options.className - Custom CSS class
 * @param {Object} options.style - Custom inline styles
 * @returns {number|string} Toast ID
 */
export const showToast = ({
    message,
    type = 'info',
    title,
    icon,
    position,
    autoClose,
    hideProgressBar,
    closeOnClick,
    pauseOnHover,
    draggable,
    transition,
    theme = 'light',
    className,
    style,
    ...otherOptions
}) => {
    // Map transition string to component
    const transitions = {
        Slide,
        Bounce,
        Flip,
        Zoom,
    };

    const toastOptions = {
        ...defaultOptions,
        position: position || defaultOptions.position,
        autoClose: autoClose !== undefined ? autoClose : defaultOptions.autoClose,
        hideProgressBar: hideProgressBar !== undefined ? hideProgressBar : defaultOptions.hideProgressBar,
        closeOnClick: closeOnClick !== undefined ? closeOnClick : defaultOptions.closeOnClick,
        pauseOnHover: pauseOnHover !== undefined ? pauseOnHover : defaultOptions.pauseOnHover,
        draggable: draggable !== undefined ? draggable : defaultOptions.draggable,
        transition: transition ? transitions[transition] || defaultOptions.transition : defaultOptions.transition,
        theme,
        className,
        style,
        ...otherOptions,
    };

    const content = (
        <CustomToastContent
            type={type}
            message={message}
            title={title}
            icon={icon}
        />
    );

    // Call the appropriate toast method
    switch (type) {
        case 'success':
            return toast.success(content, toastOptions);
        case 'error':
            return toast.error(content, toastOptions);
        case 'warning':
            return toast.warning(content, toastOptions);
        case 'info':
            return toast.info(content, toastOptions);
        default:
            return toast(content, toastOptions);
    }
};

/**
 * Convenience methods for different toast types
 */
export const Toast = {
    success: (message, options = {}) =>
        showToast({ message, type: 'success', ...options }),

    error: (message, options = {}) =>
        showToast({ message, type: 'error', ...options }),

    info: (message, options = {}) =>
        showToast({ message, type: 'info', ...options }),

    warning: (message, options = {}) =>
        showToast({ message, type: 'warning', ...options }),

    /**
     * Show a custom toast with full control
     */
    custom: (content, options = {}) => {
        return toast(content, { ...defaultOptions, ...options });
    },

    /**
     * Show a promise toast
     * @param {Promise} promise - The promise to track
     * @param {Object} messages - Messages for pending, success, and error states
     */
    promise: (promise, { pending, success, error }, options = {}) => {
        return toast.promise(
            promise,
            {
                pending: {
                    render: () => <CustomToastContent type="info" message={pending} />,
                },
                success: {
                    render: ({ data }) => (
                        <CustomToastContent
                            type="success"
                            message={typeof success === 'function' ? success(data) : success}
                        />
                    ),
                },
                error: {
                    render: ({ data }) => (
                        <CustomToastContent
                            type="error"
                            message={typeof error === 'function' ? error(data) : (data?.message || error)}
                        />
                    ),
                },
            },
            { ...defaultOptions, ...options }
        );
    },

    /**
     * Dismiss a specific toast or all toasts
     * @param {number|string} toastId - Optional toast ID to dismiss specific toast
     */
    dismiss: (toastId) => {
        if (toastId) {
            toast.dismiss(toastId);
        } else {
            toast.dismiss();
        }
    },

    /**
     * Update an existing toast
     * @param {number|string} toastId - Toast ID to update
     * @param {Object} options - New options
     */
    update: (toastId, content, options = {}) => {
        toast.update(toastId, {
            ...options,
            render: React.isValidElement(content)
                ? content
                : (
                    <CustomToastContent
                        type={options.type || 'info'}
                        message={content}
                        title={options.title}
                        icon={options.icon}
                    />
                ),
        });
    },

    /**
     * Check if a toast is active
     * @param {number|string} toastId - Toast ID
     * @returns {boolean}
     */
    isActive: (toastId) => {
        return toast.isActive(toastId);
    },
};

export default Toast;
